import { ChangeEvent, FocusEvent, useState } from "react";

import { SpecimenProtocolOverride } from "pwapi";

import { AccessionSpecimen } from "custom-models";

import { getProtocolStatus } from "shared/utils/specimenUtils";

export const useSlowMohs = (
  specimenValues: AccessionSpecimen,
  specimen: AccessionSpecimen | undefined,
  specimens: AccessionSpecimen[],
  isCaseAccessioned: boolean | undefined,
  setSpecimensValues: Function
) => {
  const [slowMohsSegmentCount, setSlowMohsSegmentCount] = useState<
    number | undefined
  >();

  const slowMohsSegmentNames = [specimenValues.name];
  if (slowMohsSegmentCount) {
    const curCharCode = specimenValues.name.charCodeAt(0);
    for (let i = 1; i < slowMohsSegmentCount; i++) {
      slowMohsSegmentNames.push(String.fromCharCode(curCharCode + i));
    }
  }

  const specimensIndex = specimen ? specimens.indexOf(specimen) : -2;
  const previousSpecimenIndex =
    (specimensIndex >= 0 ? specimensIndex : specimens.length) - 1;
  const previousSpecimen =
    previousSpecimenIndex >= 0
      ? specimens.at(previousSpecimenIndex)
      : undefined;
  const previousSpecimenIsSlowMohs =
    previousSpecimen &&
    getProtocolStatus(
      previousSpecimen,
      SpecimenProtocolOverride.protocol.SLOW_MOHS
    ).active;
  const slowMohsByName =
    !isCaseAccessioned ||
    (!specimen?.id && specimenValues.slow_mohs_source_id === undefined);
  const lastSlowMohsGroup = previousSpecimenIsSlowMohs
    ? specimens.filter((s) => {
        if (slowMohsByName)
          return [s.name, s.slow_mohs_source_name].includes(
            previousSpecimen.slow_mohs_source_name || previousSpecimen.name
          );
        return [s.id, s.slow_mohs_source_id].includes(
          previousSpecimen?.slow_mohs_source_id || 0
        );
      })
    : [];
  const partOfLastSlowMohsGroup = slowMohsByName
    ? [
        previousSpecimen?.name,
        previousSpecimen?.slow_mohs_source_name,
      ].includes(specimenValues?.slow_mohs_source_name || "")
    : [previousSpecimen?.id, previousSpecimen?.slow_mohs_source_id].includes(
        specimenValues.slow_mohs_source_id
      );
  if (specimen && lastSlowMohsGroup.indexOf(specimen) === -1)
    lastSlowMohsGroup.push(specimenValues);
  const isLastSpecimen =
    specimensIndex < 0 || specimensIndex === specimens.length - 1;

  const protocolTagsSpecimens = slowMohsSegmentCount
    ? slowMohsSegmentNames.map((name) => ({
        name,
        slow_mohs_source_name: specimenValues.name,
      }))
    : [
        ...specimens,
        ...(!specimens.some((s) => s.name === specimenValues.name)
          ? [specimenValues]
          : []),
      ];

  if (
    getProtocolStatus(
      specimenValues,
      SpecimenProtocolOverride.protocol.SLOW_MOHS
    ).active &&
    !specimenValues.slow_mohs_source_name &&
    !specimenValues.slow_mohs_source_id
  ) {
    setSpecimensValues({
      ...specimenValues,
      slow_mohs_source_name: specimenValues.name,
    });
  }

  const onChangeSlowMohsAddToGroup = (_ev: ChangeEvent<HTMLInputElement>) => {
    if (!partOfLastSlowMohsGroup) {
      setSlowMohsSegmentCount(undefined);
      if (!isCaseAccessioned) {
        setSpecimensValues({
          ...specimenValues,
          slow_mohs_source_name: previousSpecimenIsSlowMohs
            ? previousSpecimen.slow_mohs_source_name || previousSpecimen.name
            : undefined,
          slow_mohs_source_id: undefined,
        });
      } else {
        setSpecimensValues({
          ...specimenValues,
          slow_mohs_source_name: undefined,
          slow_mohs_source_id: previousSpecimenIsSlowMohs
            ? previousSpecimen.slow_mohs_source_id || previousSpecimen.id
            : undefined,
        });
      }
    } else {
      setSpecimensValues({
        ...specimenValues,
        slow_mohs_source_name: specimenValues.name,
        slow_mohs_source_id: specimen?.id,
      });
    }
  };

  const onFocusSlowMohsCreateGroup = (ev: FocusEvent<HTMLInputElement>) => {
    if (ev.target.value) return;
    setSlowMohsSegmentCount(5);
    setSpecimensValues({
      ...specimenValues,
      slow_mohs_source_name: specimenValues.name,
      slow_mohs_source_id: specimen?.id,
    });
  };

  const onChangeSlowMohsCreateGroup = (ev: ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(ev.target.value) || undefined;
    setSlowMohsSegmentCount(value);
    setSpecimensValues({
      ...specimenValues,
      slow_mohs_source_name: specimenValues.name,
      slow_mohs_source_id: specimen?.id,
    });
  };

  return {
    slowMohsSegmentCount,
    previousSpecimenIsSlowMohs,
    partOfLastSlowMohsGroup,
    isLastSpecimen,
    protocolTagsSpecimens,
    onChangeSlowMohsAddToGroup,
    onFocusSlowMohsCreateGroup,
    onChangeSlowMohsCreateGroup,
  };
};

export default useSlowMohs;
